import { Controller } from "react-hook-form";
import type { Control, FieldErrors, UseFormSetValue } from "react-hook-form";

import type {
  CourtOptionPaginationControls,
  SearchOption,
  SearchOptionPaginationControl,
} from "../../types/searchSessions";
import type { CauseFormData } from "./causeList.types";
import SearchableSelect from "../SearchableSelect";

interface CauseCourtFieldsProps {
  control: Control<CauseFormData>;
  setValue: UseFormSetValue<CauseFormData>;
  errors: FieldErrors<CauseFormData>;
  courtType: string;
  highcourt: string;
  state: string;
  bench: string;
  benchRoom: string;
  district: string;
  complex: string;
  establishment: string;
  courtRoom: string;
  highCourts: SearchOption[];
  states: SearchOption[];
  benches: SearchOption[];
  benchRooms: SearchOption[];
  districts: SearchOption[];
  complexes: SearchOption[];
  establishments: SearchOption[];
  courtRooms: SearchOption[];
  courtEstablishment?: boolean;
  controls?: CourtOptionPaginationControls;
  selectedLabels?: Partial<
    Record<
      | "highcourt"
      | "state"
      | "bench"
      | "benchRoom"
      | "district"
      | "complex"
      | "establishment"
      | "courtRoom",
      string
    >
  >;
}

const CauseCourtFields = ({
  control,
  setValue,
  errors,
  courtType,
  highcourt,
  state,
  bench,
  benchRoom,
  district,
  complex,
  establishment,
  courtRoom,
  highCourts,
  states,
  benches,
  benchRooms,
  districts,
  complexes,
  establishments,
  courtRooms,
  courtEstablishment,
  controls = {},
  selectedLabels = {},
}: CauseCourtFieldsProps) => {
  const handleCourtTypeChange = (value: string) => {
    setValue("courtType", value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    clearCourtValues(setValue);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
        Court Information
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Court Selection *
          </label>
          <Controller
            name="courtType"
            control={control}
            rules={{ required: "Court selection is required" }}
            render={({ field }) => (
              <select
                {...field}
                onChange={(event) => handleCourtTypeChange(event.target.value)}
                className={fieldClassName(Boolean(errors.courtType))}
              >
                <option value="">Select Court Type</option>
                <option value="High Court">High Court</option>
                <option value="District Court">District Court</option>
              </select>
            )}
          />
          {errors.courtType && (
            <p className="text-red-500 text-sm mt-1">
              {errors.courtType.message}
            </p>
          )}
        </div>

        {courtType === "High Court" && (
          <>
            <SelectField
              control={control}
              name="highcourt"
              label="High Court"
              options={highCourts}
              value={highcourt}
              selectedLabel={selectedLabels.highcourt}
              rules={{ required: "High Court is required" }}
              onValueChange={() => {
                setValue("bench", "");
                setValue("benchRoom", "");
                setValue("courtRoomNo", "");
              }}
              error={errors.highcourt?.message}
              paginationControl={controls.highCourts}
            />
            <SelectField
              control={control}
              name="bench"
              label="Bench"
              options={benches}
              value={bench}
              selectedLabel={selectedLabels.bench}
              disabled={!highcourt}
              rules={{ required: "Bench is required" }}
              onValueChange={() => {
                setValue("benchRoom", "");
                setValue("courtRoomNo", "");
              }}
              error={errors.bench?.message}
              paginationControl={controls.benches}
            />
            <SelectField
              control={control}
              name="benchRoom"
              label="Bench Rooms"
              options={benchRooms}
              value={benchRoom}
              selectedLabel={selectedLabels.benchRoom}
              disabled={!bench}
              rules={{ required: "Bench room is required" }}
              onValueChange={(nextValue) => {
                const selectedBenchRoom = benchRooms.find(
                  (room) => room.value === nextValue,
                );
                setValue("courtRoomNo", selectedBenchRoom?.label || "");
              }}
              error={errors.benchRoom?.message}
              paginationControl={controls.benchRooms}
            />
          </>
        )}

        {courtType === "District Court" && (
          <>
            <SelectField
              control={control}
              name="state"
              label="State"
              options={states}
              value={state}
              selectedLabel={selectedLabels.state}
              rules={{ required: "State is required" }}
              onValueChange={() => {
                setValue("district", "");
                setValue("complex", "");
                setValue("establishment", "");
                setValue("courtRoom", "");
              }}
              error={errors.state?.message}
            />
            <SelectField
              control={control}
              name="district"
              label="District"
              options={districts}
              value={district}
              selectedLabel={selectedLabels.district}
              disabled={!state}
              rules={{ required: "District is required" }}
              onValueChange={() => {
                setValue("complex", "");
                setValue("establishment", "");
                setValue("courtRoom", "");
              }}
              error={errors.district?.message}
              paginationControl={controls.districts}
            />
            <SelectField
              control={control}
              name="complex"
              label="Court Complex"
              options={complexes}
              value={complex}
              selectedLabel={selectedLabels.complex}
              disabled={!district}
              rules={{ required: "Court complex is required" }}
              onValueChange={() => {
                setValue("establishment", "");
                setValue("courtRoom", "");
              }}
              error={errors.complex?.message}
              paginationControl={controls.complexes}
            />
            {courtEstablishment && (
              <SelectField
                control={control}
                name="establishment"
                label="Establishment"
                options={establishments}
                value={establishment}
                selectedLabel={selectedLabels.establishment}
                disabled={!complex}
                rules={{ required: "Establishment is required" }}
                onValueChange={() => setValue("courtRoom", "")}
                error={errors.establishment?.message}
                paginationControl={controls.establishments}
              />
            )}
            <SelectField
              control={control}
              name="courtRoom"
              label="Court Room"
              options={courtRooms}
              value={courtRoom}
              selectedLabel={selectedLabels.courtRoom}
              disabled={
                !complex || (Boolean(courtEstablishment) && !establishment)
              }
              rules={{ required: "Court room is required" }}
              error={errors.courtRoom?.message}
              paginationControl={controls.courtRooms}
            />
          </>
        )}
      </div>
    </div>
  );
};

interface SelectFieldProps {
  control: Control<CauseFormData>;
  name: keyof CauseFormData;
  label: string;
  options: SearchOption[];
  value: string;
  selectedLabel?: string;
  rules?: Record<string, unknown>;
  onValueChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
  paginationControl?: SearchOptionPaginationControl;
}

const SelectField = ({
  control,
  name,
  label,
  options,
  value,
  selectedLabel,
  rules,
  onValueChange,
  error,
  disabled = false,
  paginationControl,
}: SelectFieldProps) => {
  const hasSelectedOption = options.some((option) => option.value === value);
  const optionsWithSelectedValue =
    value && !hasSelectedOption
      ? [
          {
            label: selectedLabel || value,
            value,
          },
          ...options,
        ]
      : options;

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-900 mb-2">
        {label} *
      </label>
      <Controller
        name={name as any}
        control={control}
        rules={rules as any}
        render={({ field }) => {
          const selectedOption =
            optionsWithSelectedValue.find(
              (option) => option.value === field.value,
            ) || null;

          return (
            <SearchableSelect
              options={optionsWithSelectedValue}
              value={selectedOption}
              onChange={(option: any) => {
                const nextValue = option?.value || "";
                field.onChange(nextValue);
                onValueChange?.(nextValue);
              }}
              disabled={disabled}
              placeholder={`Select ${label}`}
              searchPlaceholder={`Search ${label.toLowerCase()}...`}
              className="w-full"
              onSearchChange={paginationControl?.onSearchChange}
              onLoadMore={paginationControl?.onLoadMore}
              hasMore={paginationControl?.hasMore}
              isLoadingMore={paginationControl?.isLoadingMore}
              isLoadingOptions={paginationControl?.isLoading}
            />
          );
        }}
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

const clearCourtValues = (setValue: UseFormSetValue<CauseFormData>) => {
  setValue("highcourt", "");
  setValue("state", "");
  setValue("bench", "");
  setValue("benchRoom", "");
  setValue("courtRoomNo", "");
  setValue("district", "");
  setValue("complex", "");
  setValue("establishment", "");
  setValue("courtRoom", "");
};

const fieldClassName = (hasError = false, disabled = false) =>
  `w-full px-4 py-3 border rounded-xl focus:ring-1 outline-0 transition-colors ${
    hasError
      ? "border-red-500 focus:ring-red-400"
      : "border-gray-200 focus:ring-blue-400 focus:border-blue-400"
  } ${disabled ? "bg-gray-50 text-gray-400 cursor-not-allowed" : ""}`;

export default CauseCourtFields;
